import type { MarketSelectorPreset } from '@src/components/MarketSelector/types';
import {
  AggregationIcon,
  BaseSelect,
  FormControlSizes,
  HStack,
  Icon,
  IconButton,
  IconName,
  Text,
  Tooltip,
  useMarketAccountsContext,
  useMarketsContext,
  type MarketAccount,
} from '@talos/kyoko';
import { identity } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { NavigableControlArrow, NavigableControlRow } from './components/NavigableControlRow';
import { SelectedFieldTooltipContent } from './components/SelectedFieldTooltipContent';
import { MARKET_CARD_CONTROL_TOOLTIP_PROPS } from './marketCardControlTooltipProps';

export const MarketCardMarketAccountButtonViewMode = {
  Icon: 'icon',
  FullWidth: 'fullWidth',
} as const;
export type MarketCardMarketAccountButtonViewMode =
  (typeof MarketCardMarketAccountButtonViewMode)[keyof typeof MarketCardMarketAccountButtonViewMode];

export function MarketCardMarketAccountButton({
  onClick,
  size = FormControlSizes.Small,
  viewMode = MarketCardMarketAccountButtonViewMode.Icon,
  showAggregationNavigation = true,
  isEditSymbolMode = false,
  label,
  selectedMarketOrAccountNames,
  selectedAggregationID,
  aggregationPresets,
  onChangeAggregation,
}: {
  onClick: () => void;
  size?: FormControlSizes;
  viewMode?: MarketCardMarketAccountButtonViewMode;
  showAggregationNavigation?: boolean;
  isEditSymbolMode?: boolean;
  label: string;
  selectedMarketOrAccountNames: string[];
  selectedAggregationID?: string;
  aggregationPresets: MarketSelectorPreset[];
  onChangeAggregation: (aggregationID: string) => void;
}) {
  const sortedAggregationPresets = useMemo(
    () => aggregationPresets.toSorted((a, b) => a.label.localeCompare(b.label)),
    [aggregationPresets]
  );
  const tooltip = <MarketCardMarketAccountTooltipContent selectedMarketOrAccountNames={selectedMarketOrAccountNames} />;
  const shouldRenderAggregationNavigation =
    !isEditSymbolMode && showAggregationNavigation && sortedAggregationPresets.length > 0;
  const currentAggregationIndex = useMemo(
    () => sortedAggregationPresets.findIndex(aggregation => aggregation.id === selectedAggregationID),
    [selectedAggregationID, sortedAggregationPresets]
  );

  const handlePreviousAggregation = useCallback(() => {
    if (!sortedAggregationPresets.length) {
      return;
    }
    let previousIndex = currentAggregationIndex - 1;
    if (currentAggregationIndex === -1 || previousIndex < 0) {
      previousIndex = sortedAggregationPresets.length - 1;
    }
    onChangeAggregation(sortedAggregationPresets[previousIndex].id);
  }, [currentAggregationIndex, onChangeAggregation, sortedAggregationPresets]);

  const handleNextAggregation = useCallback(() => {
    if (!sortedAggregationPresets.length) {
      return;
    }
    let nextIndex = currentAggregationIndex + 1;
    if (currentAggregationIndex === -1 || nextIndex > sortedAggregationPresets.length - 1) {
      nextIndex = 0;
    }
    onChangeAggregation(sortedAggregationPresets[nextIndex].id);
  }, [currentAggregationIndex, onChangeAggregation, sortedAggregationPresets]);

  if (viewMode === MarketCardMarketAccountButtonViewMode.FullWidth) {
    const content = (
      <MarketSelectorBaseSelect
        value={label}
        getLabel={identity}
        customButtonLayout={props => (
          <MarketSelectorButtonLayout item={props.item} isEditSymbolMode={isEditSymbolMode} />
        )}
        isDropdownOpened={false}
        onClick={onClick}
        showChevron={false}
        centered
        size={size}
        width="100%"
        dataTestID="market-card-market-acct-button"
      />
    );

    return (
      <NavigableControlRow
        left={
          shouldRenderAggregationNavigation ? (
            <NavigableControlArrow
              direction="previous"
              size={size}
              className="aggregation-prev"
              dataTestID="aggregation-prev"
              tooltip="Previous Favorite Aggregation"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handlePreviousAggregation();
              }}
            />
          ) : undefined
        }
        right={
          shouldRenderAggregationNavigation ? (
            <NavigableControlArrow
              direction="next"
              size={size}
              className="aggregation-next"
              dataTestID="aggregation-next"
              tooltip="Next Favorite Aggregation"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleNextAggregation();
              }}
            />
          ) : undefined
        }
      >
        {isEditSymbolMode ? (
          content
        ) : (
          <Tooltip tooltip={tooltip} {...MARKET_CARD_CONTROL_TOOLTIP_PROPS}>
            {content}
          </Tooltip>
        )}
      </NavigableControlRow>
    );
  }

  if (isEditSymbolMode) {
    return (
      <MarketCardMarketAccountButtonElement
        aria-label="Edit Symbol"
        data-testid="market-card-market-acct-button"
        icon={IconName.Pencil}
        onClick={onClick}
        size={size}
      />
    );
  }

  return (
    <NavigableControlRow
      left={
        shouldRenderAggregationNavigation ? (
          <NavigableControlArrow
            direction="previous"
            size={size}
            className="aggregation-prev"
            dataTestID="aggregation-prev"
            tooltip="Previous Favorite Aggregation"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handlePreviousAggregation();
            }}
          />
        ) : undefined
      }
      right={
        shouldRenderAggregationNavigation ? (
          <NavigableControlArrow
            direction="next"
            size={size}
            className="aggregation-next"
            dataTestID="aggregation-next"
            tooltip="Next Favorite Aggregation"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleNextAggregation();
            }}
          />
        ) : undefined
      }
    >
      <Tooltip tooltip={tooltip} {...MARKET_CARD_CONTROL_TOOLTIP_PROPS}>
        <MarketCardMarketAccountButtonElement
          aria-label="Open Market Selector"
          data-testid="market-card-market-acct-button"
          icon={IconName.Scale}
          color="colors.purple.lighten"
          onClick={onClick}
          size={size}
        />
      </Tooltip>
    </NavigableControlRow>
  );
}

function MarketCardMarketAccountTooltipContent({
  selectedMarketOrAccountNames,
}: {
  selectedMarketOrAccountNames: string[];
}) {
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const lines = useMemo(() => {
    return selectedMarketOrAccountNames
      .map(selectionName => getSelectionDisplayName(selectionName, marketAccountsByName, marketsByName))
      .toSorted((a, b) => a.localeCompare(b));
  }, [selectedMarketOrAccountNames, marketAccountsByName, marketsByName]);

  return (
    <SelectedFieldTooltipContent
      title="Selected Markets and Accounts"
      lines={lines}
      emptyMessage="No selected markets or accounts"
    />
  );
}

function getSelectionDisplayName(
  selectionName: string,
  marketAccountsByName: Map<string, MarketAccount>,
  marketsByName: ReturnType<typeof useMarketsContext>['marketsByName']
) {
  return (
    marketAccountsByName.get(selectionName)?.DisplayName ??
    marketsByName.get(selectionName)?.DisplayName ??
    selectionName
  );
}

export const MarketCardMarketAccountButtonElement = styled(IconButton)``;
const MarketSelectorBaseSelect = styled(BaseSelect<string>)`
  width: 100%;
`;
const MarketSelectorButtonLayout = ({
  item,
  isEditSymbolMode,
}: {
  item: string | undefined;
  isEditSymbolMode: boolean;
}) => {
  return (
    <MarketSelectorButtonContent gap="spacing4">
      {isEditSymbolMode ? <Icon icon={IconName.Pencil} /> : <AggregationIcon />}
      <MarketSelectorLabel title={item}>{item}</MarketSelectorLabel>
    </MarketSelectorButtonContent>
  );
};
const MarketSelectorButtonContent = styled(HStack)`
  min-width: 0;
  width: 100%;
  justify-content: center;
`;
const MarketSelectorLabel = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
